import React, { useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import { AlertUnsavedData, Modal, ModalSize, NotificationToaster } from "components/notifications";
import { FormTextInput, FormSelect } from "components/form-fields";
import { Button, ButtonSave } from "components/buttons";
import { useDirtyData } from "hooks/useDirtyData";
import { useValidation } from "hooks/useValidation";
import { PcnService } from "services/PcnService";
import { Intent } from "@blueprintjs/core";

const defaultArray = [];

const contraventionCodeMaxLength = 20;
const contraventionDescriptionMaxLength = 255;
const badRequestCode = 400;

const defaultModel = {
    id: 0,
    contraventionCode: "",
    contraventionDescription: "",
    responsibilityKey: null
};

export function PcnContraventionCodeModal(
    {
        showModal,
        modalTitle,
        isUpdate = false,
        loading = false,
        responsibilities = defaultArray,
        onSave,
        onCloseModal,
        contraventionCodeDetails = defaultModel
    }
) {
    const [original, setOriginal] = useState(defaultModel);
    const [current, setCurrent] = useState(defaultModel);
    const [saving, setSaving] = useState(false);
    const [blockModalClose, setBlockModalClose] = useState(false);
    const isDirty = useDirtyData(original, current);
    const [isValid, errors, validate] = useValidation();

    useEffect(loadDetails, [contraventionCodeDetails, showModal]);

    function loadDetails() {
        if (!showModal) {
            return;
        }

        const mapped = {
            id: contraventionCodeDetails.id || defaultModel.id,
            contraventionCode: contraventionCodeDetails.contraventionCode || defaultModel.contraventionCode,
            contraventionDescription: contraventionCodeDetails.contraventionDescription || defaultModel.contraventionDescription,
            responsibilityKey: contraventionCodeDetails.responsibilityKey || defaultModel.responsibilityKey
        };

        setOriginal(mapped);
        setCurrent(mapped);
    }

    function onFieldChange(fieldName, passedValue) {
        setCurrent({
            ...current,
            [fieldName]: passedValue
        });
    }

    useEffect(runValidation, [current, showModal, loading]);

    function runValidation() {
        if (!showModal || loading) {
            return;
        }

        const rules = [
            { fieldName: "contraventionCode", required: true, maxLength: contraventionCodeMaxLength },
            { fieldName: "contraventionDescription", required: true, maxLength: contraventionDescriptionMaxLength },
            { fieldName: "responsibilityKey", required: true }
        ];

        validate(rules, current);
    }

    function onSaveClick() {
        if (!isValid || !isDirty || saving || loading) {
            return;
        }

        const request = {
            contraventionCode: current.contraventionCode,
            contraventionDescription: current.contraventionDescription,
            responsibilityKey: current.responsibilityKey
        };

        setSaving(true);

        const savePromise = isUpdate
            ? PcnService.updateContraventionCode(current.id, request)
            : PcnService.createContraventionCode(request);

        savePromise
            .then(() => {
                NotificationToaster.show(Intent.SUCCESS, isUpdate ? "Contravention code updated successfully." : "Contravention code created successfully.");
                onSaveComplete();
            })
            .catch((error) => {
                if (error?.response?.status === badRequestCode) {
                    NotificationToaster.show(Intent.WARNING, error.response.data);
                } else {
                    NotificationToaster.show(Intent.DANGER, "Could not save the contravention code. Please try again.");
                }
            })
            .finally(() => {
                setSaving(false);
            });
    }

    function onSaveComplete() {
        resetModal();
        onSave();
    }

    function onCloseClick(forceClose = false) {
        if (isDirty && !forceClose) {
            setBlockModalClose(true);
            return;
        }

        resetModal();

        if (onCloseModal) {
            onCloseModal();
        }
    }

    function resetModal() {
        setBlockModalClose(false);
        setOriginal(defaultModel);
        setCurrent(defaultModel);
    }

    return (
        <Fragment>
            <Modal
                isOpen={showModal}
                onClose={() => onCloseClick(false)}
                clickOutsideClose={true}
                title={modalTitle}
                size={ModalSize.MEDIUM}
                updateStateOnClose={false}
                footer={(
                    <div className="bp6-dialog-footer-actions">
                        <Button
                            text="Cancel"
                            intent={Intent.SECONDARY}
                            onClick={() => onCloseClick(true)}
                        />
                        <ButtonSave
                            id="save-contravention-code-button"
                            onClick={onSaveClick}
                            simpleDisabled={!isValid || loading || !isDirty}
                            disabled={saving}
                            text={"Save"}
                        />
                    </div>
                )}
            >
                <FormTextInput
                    id="input-contravention-code"
                    disabled={saving}
                    loading={loading}
                    value={current.contraventionCode}
                    headingText="Contravention Code"
                    placeholder="Please type the contravention code here"
                    onChange={(event) => onFieldChange("contraventionCode", event.target.value)}
                    dangerHelperText={errors.contraventionCode}
                    maxLength={contraventionCodeMaxLength}
                    large={true}
                    fill={true}
                />

                <FormTextInput
                    id="input-contravention-description"
                    disabled={saving}
                    loading={loading}
                    value={current.contraventionDescription}
                    headingText="Description"
                    placeholder="Please type the description here"
                    onChange={(event) => onFieldChange("contraventionDescription", event.target.value)}
                    dangerHelperText={errors.contraventionDescription}
                    maxLength={contraventionDescriptionMaxLength}
                    large={true}
                    fill={true}
                />

                <FormSelect
                    id="select-responsibility"
                    disabled={saving}
                    loading={loading}
                    items={responsibilities}
                    selectedValue={current.responsibilityKey}
                    headingText="Responsibility"
                    onItemSelect={(item) => onFieldChange("responsibilityKey", item.id)}
                    dangerHelperText={errors.responsibilityKey}
                    fill={true}
                />
            </Modal>

            <AlertUnsavedData
                isDirty={isDirty}
                isDirtySamePage={blockModalClose}
                onCancelLeave={() => setBlockModalClose(false)}
                onConfirmLeave={() => onCloseClick(true)}
            />
        </Fragment>
    );
}

PcnContraventionCodeModal.propTypes = {
    showModal: PropTypes.bool.isRequired,
    modalTitle: PropTypes.string.isRequired,
    isUpdate: PropTypes.bool,
    loading: PropTypes.bool,
    responsibilities: PropTypes.array,
    onSave: PropTypes.func.isRequired,
    onCloseModal: PropTypes.func.isRequired,
    contraventionCodeDetails: PropTypes.object
};
